import type { IListenable } from "../../../interfaces/IListenable";
import type { IMeasurable } from "../../../interfaces/IMeasurable";
import { Node } from "../../Core/Node";
import { Group } from "../../Core/Group";
import { throttle } from "@/map/utils/EventUtils";

const DEFAULT_USE_THROTTLE = true; // 是否使用节流函数
const DEFAULT_THROTTLE_WAIT = 16; // 节流函数的等待时间，单位为毫秒(8ms大约是120fps，16ms大约是60fps, 32ms大约是30fps)

export enum MouseButton {
    Left = 0,    // 左键
    Middle = 1,  // 中键
    Right = 2    // 右键
}

export type DragEventData = {
    originalEvent: MouseEvent; // 原始事件
    button: MouseButton; // 鼠标按键
    mouseX: number; // 鼠标按下时的 x 坐标
    mouseY: number; // 鼠标按下时的 y 坐标
    lastX?: number; // 上一个鼠标位置
    lastY?: number; // 上一个鼠标位置
    deltaX?: number; // 相对于上一个位置的鼠标移动的增量
    deltaY?: number; // 相对于上一个位置的鼠标移动的增量
    node?: Node; // 被拖拽的节点
}

export abstract class BaseDraggable {
    /** 绑定的 DOM 包装器对象（兼具事件监听和尺寸测量能力） */
    protected domWrapper: IListenable & IMeasurable;
    /** 当前是否处于拖拽状态 */
    protected isDragging: boolean = false;
    /** 鼠标按下时的 x 坐标 */
    protected mouseX: number = 0;
    /** 鼠标按下时的 y 坐标 */
    protected mouseY: number = 0;
    /** 上一次记录的鼠标 x 位置（用于计算增量） */
    protected lastX: number = 0;
    /** 上一次记录的鼠标 y 位置（用于计算增量） */
    protected lastY: number = 0;
    /** 原始光标样式（拖拽开始前保存） */
    protected originalCursor: string = '';
    /** 当前激活的鼠标按键 */
    protected activeButton: MouseButton | null = null;

    // 需子类实现的抽象方法
    protected abstract handleMouseDown(e: MouseEvent): void;
    protected abstract handleMouseMove(e: MouseEvent): void;
    protected abstract handleMouseUp(e: MouseEvent): void;

    // 事件处理器引用
    private _mouseDownHandler: (e: MouseEvent) => void;
    private _mouseMoveHandler: (e: MouseEvent) => void;
    private _mouseUpHandler: (e: MouseEvent) => void;
    private _throttledMouseMoveHandler: (e: MouseEvent) => void;

    private useThrottle: boolean = DEFAULT_USE_THROTTLE; // 是否使用节流功能
    private throttleWait: number = DEFAULT_THROTTLE_WAIT; // 节流等待时间（毫秒）

    /**
     * 构造函数
     * @param target - 绑定的目标元素（默认 document.body）
     * @param options - 配置选项
     * @param options.useThrottle - 是否启用节流
     * @param options.throttleWait - 节流等待时间
     */
    constructor(
        target?: IListenable & IMeasurable,
        options?: {
            useThrottle?: boolean;
            throttleWait?: number;
        }
    ) {
        this.domWrapper = target || document.body;

        // 应用选项配置
        if (options) {
            const { useThrottle, throttleWait } = options;
            if (useThrottle !== undefined) this.useThrottle = useThrottle;
            if (throttleWait !== undefined) this.throttleWait = throttleWait;
        }

        // 绑定事件处理方法
        this._mouseDownHandler = this._handleMouseDown.bind(this);

        // 使用节流包装鼠标移动处理函数
        this._mouseMoveHandler = this._handleMouseMove.bind(this);
        this._throttledMouseMoveHandler = throttle(this._mouseMoveHandler, this.throttleWait, {
            leading: true,
            trailing: true
        });

        this._mouseUpHandler = this._handleMouseUp.bind(this);

        // 添加事件监听器
        this.domWrapper.addEventListener('mousedown', this._mouseDownHandler);
    }

    // 包装方法，处理鼠标按下事件并添加其他事件监听器
    private _handleMouseDown(e: MouseEvent): void {
        // 调用子类实现的处理逻辑
        this.handleMouseDown(e);

        // 根据 useThrottle 决定使用哪个处理函数
        const moveHandler = this.useThrottle ? this._throttledMouseMoveHandler : this._mouseMoveHandler;

        // 添加拖拽相关的事件监听器
        document.addEventListener('mousemove', moveHandler);
        document.addEventListener('mouseup', this._mouseUpHandler);
    }

    // 包装方法，处理鼠标移动事件
    private _handleMouseMove(e: MouseEvent): void {
        // 调用子类实现的处理逻辑
        this.handleMouseMove(e);
    }

    // 包装方法，处理鼠标松开事件并移除事件监听器
    private _handleMouseUp(e: MouseEvent): void {
        // 调用子类实现的处理逻辑
        this.handleMouseUp(e);

        // 根据 useThrottle 决定移除哪个处理函数
        const moveHandler = this.useThrottle ? this._throttledMouseMoveHandler : this._mouseMoveHandler;

        // 移除拖拽相关的事件监听器
        document.removeEventListener('mousemove', moveHandler);
        document.removeEventListener('mouseup', this._mouseUpHandler);
    }

    /**
     * 资源清理方法（移除所有事件监听器）
     */
    protected dispose(): void {
        this.domWrapper.removeEventListener('mousedown', this._mouseDownHandler);
        // 确保移除所有可能的事件监听器
        document.removeEventListener('mousemove', this._throttledMouseMoveHandler);
        document.removeEventListener('mousemove', this._mouseMoveHandler);
        document.removeEventListener('mouseup', this._mouseUpHandler);
    }

    /**
     * 设置是否使用节流功能
     * @param use - 是否启用节流
     */
    public setUseThrottle(use: boolean): void {
        // 如果当前正在拖拽中，不允许修改设置
        if (this.isDragging) {
            console.warn('不能在拖拽过程中修改节流设置');
            return;
        }
        this.useThrottle = use;
    }

    /**
     * 设置节流等待时间
     * @param wait - 等待时间（毫秒）
     */
    public setThrottleWait(wait: number): void {
        this.throttleWait = wait;
        // 重新创建节流函数
        this._throttledMouseMoveHandler = throttle(this._mouseMoveHandler, this.throttleWait, {
            leading: true,
            trailing: true
        });
    }

    /**
     * 围绕原点旋转一个点
     * @param x - 点的 x 坐标
     * @param y - 点的 y 坐标
     * @param angle - 旋转角度（弧度）
     * @returns 旋转后的点坐标 { x: number, y: number }
     */
    private _rotatePoint(x: number, y: number, angle: number): { x: number, y: number } {
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
        return {
            x: x * cos - y * sin,
            y: x * sin + y * cos
        };
    }

    /**
     * 共用的点击检测逻辑
     * @param x 鼠标点击的 x 坐标
     * @param y 鼠标点击的 y 坐标
     * @param node 被点击的节点
     * @returns 如果点击在节点内返回 true，否则返回 false
     */
    protected isPointInNode(x: number, y: number, node: Node): boolean {
        const { x: glbX, y: glbY } = node.globalPosition;
        const { x: glbScaleX, y: glbScaleY } = node.globalScaling;
        const width = node.width * glbScaleX;
        const height = node.height * glbScaleY;
        const glbRotation = node.globalRotation;

        // 如果没有旋转，使用简单的矩形检测
        if (glbRotation === 0) {
            return (
                x >= glbX &&
                x <= glbX + width &&
                y >= glbY &&
                y <= glbY + height
            );
        }

        // 对于旋转的节点，将鼠标点转换到节点的局部坐标系
        // 1. 平移到原点
        const localX = x - glbX;
        const localY = y - glbY;

        // 2. 逆向旋转
        const rotatedPoint = this._rotatePoint(localX, localY, -glbRotation);

        // 3. 在局部坐标中检查点是否在矩形内
        return (
            rotatedPoint.x >= 0 &&
            rotatedPoint.x <= width &&
            rotatedPoint.y >= 0 &&
            rotatedPoint.y <= height
        );
    }

    // 添加边界检查方法（考虑旋转）
    protected checkBoundaries(deltaX: number, deltaY: number, node: Node): {
        x: number, y: number
    } {
        const parent = node.parent;

        // 如果没有父节点，或不是 Group 类型，或允许溢出，则返回原始增量
        if (!parent || !(parent instanceof Group) || (parent as Group).overflowable) {
            return { x: deltaX, y: deltaY };
        }

        // 计算父容器在全局下的 AABB
        const parentAABB = this.getAABB(parent, 0, 0);
        // 计算节点移动后的 AABB（考虑旋转和缩放）
        const childAABB = this.getAABB(node, deltaX, deltaY);

        let adjustedDeltaX = deltaX;
        let adjustedDeltaY = deltaY;

        // 调整水平方向
        if (childAABB.minX < parentAABB.minX) {
            adjustedDeltaX += (parentAABB.minX - childAABB.minX);
        } else if (childAABB.maxX > parentAABB.maxX) {
            adjustedDeltaX += (parentAABB.maxX - childAABB.maxX);
        }

        // 调整垂直方向
        if (childAABB.minY < parentAABB.minY) {
            adjustedDeltaY += (parentAABB.minY - childAABB.minY);
        } else if (childAABB.maxY > parentAABB.maxY) {
            adjustedDeltaY += (parentAABB.maxY - childAABB.maxY);
        }

        return { x: adjustedDeltaX, y: adjustedDeltaY };
    }

    /**
     * 根据节点状态及额外偏移量 deltaX 和 deltaY 计算旋转后的 AABB
     */
    private getAABB(node: Node, deltaX: number, deltaY: number): {
        minX: number, minY: number, maxX: number, maxY: number
    } {
        const pos = node.globalPosition;
        const scale = node.globalScaling;
        const rotation = node.globalRotation;
        const w = node.width, h = node.height;

        // 定义局部坐标系中节点的四个角点
        const corners = [
            { x: 0, y: 0 },
            { x: w, y: 0 },
            { x: w, y: h },
            { x: 0, y: h }
        ];

        // 将角点进行缩放、旋转后平移到全局坐标系
        const transformed = corners.map(corner => {
            const xScaled = corner.x * scale.x;
            const yScaled = corner.y * scale.y;
            const rotatedPoint = this._rotatePoint(xScaled, yScaled, rotation);
            return {
                x: pos.x + deltaX + rotatedPoint.x,
                y: pos.y + deltaY + rotatedPoint.y
            };
        });

        const xs = transformed.map(p => p.x);
        const ys = transformed.map(p => p.y);
        return {
            minX: Math.min(...xs),
            maxX: Math.max(...xs),
            minY: Math.min(...ys),
            maxY: Math.max(...ys)
        };
    }
}